import React, { useContext } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { AgeFromDateString } from 'age-calculator'
import { CustomerReservationType, CustomerType } from '@/models/customer-manager/customer'
import { customerOccupation } from '@/libs/occupations'
import { DropDownMene } from '@/components/molecules/drop-down-menu'
import { deleteCustomerInfo } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import { useHistory } from 'react-router-dom'
import { nationality } from '@/libs/nationality'

type PersonInfoProps = {
  customer: CustomerType | undefined
  reservations: CustomerReservationType[]
  onClickEditCustomerInfo: () => void
}

export const PersonInfo: React.FC<PersonInfoProps> = ({ customer, reservations, onClickEditCustomerInfo = () => {} }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const history = useHistory()

  let displayAddress = ''
  if (customer?.address) {
    displayAddress = customer?.address
  } else {
    displayAddress = '-'
  }

  const agreementText = () => {
    if (customer?.deliveryAgreementStatus === 'unconfirmed') {
      return t('Unconfirmed')
    }

    if (customer?.deliveryAgreementStatus === 'deliverable') {
      return t('Deliverable')
    }

    if (customer?.deliveryAgreementStatus === 'nonDeliverable') {
      return t('Non deliverable')
    }

    return '-'
  }

  const confirmAndDeleteCustomer = async () => {
    const confirmMessage = [t('Remove customer information? This operation cannot be undone.')]
    if (reservations.length > 0) {
      confirmMessage.unshift(t('Reservations are available.'))
    }
    if (!window.confirm(confirmMessage.join('\n'))) {
      return
    }
    try {
      setIsLoading(true)
      await deleteCustomerInfo(customer!.id)
      history.replace({
        pathname: '/customer/list',
        search: window.location.search,
      })
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div css={listImageSectionStyle}>
        <img css={imageStyle} src={customer?.picture ? customer?.picture : require('@/static/images/user.svg')} />
      </div>

      <div css={listNameSectionStyle}>
        <ruby>{customer?.nameKana ? customer?.nameKana : '-'}</ruby>
        <div css={nameStyle}>{customer?.name ? customer?.name : '-'}</div>
        <div css={userAgeStyle}>
          <div>{customer?.gender ? (customer?.gender === 'M' ? t('Male') : t('Female')) : '-'} / </div>
          <div>{customer?.birthDate ? `${new AgeFromDateString(customer?.birthDate).age} ${t('Age')}` : '-'}</div>
        </div>
      </div>

      <div css={listSectionStyle}>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/birthday.svg')} />
          <p>{customer?.birthDate ? dayjs(customer?.birthDate).format('YYYY/MM/DD') : '-'}</p>
        </div>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/nationality.svg')} />
          <p>
            {customer?.nationality ? (customer?.nationality === 'JPN' ? t('Japanese nationality') : t('Foreign nationality')) : '-'}
            {customer?.nationality && customer.nationality !== 'JPN' ? `/ ${nationality[customer.nationality]?.ja || '-'}` : ''}
          </p>
        </div>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/access.svg')} />
          <p>{displayAddress}</p>
        </div>
      </div>

      <div css={listSectionStyle}>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/phone.svg')} />
          <p>{customer?.telephone?.replace(/(\d{3})(\d{3})(\d{4})/, '$1$2$3')}</p>
        </div>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/mail.svg')} />
          <p>{customer?.email ? customer?.email : '-'}</p>
        </div>
        <div css={itemWrapperStyle}>
          <img src={require('@/static/images/business.svg')} />
          <p>{customerOccupation(customer)}</p>
        </div>
        <div css={[itemWrapperStyle, { marginTop: 14 }]}>
          <img src={require('@/static/images/delivery.svg')} />
          <p>{agreementText()}</p>
        </div>
      </div>

      <DropDownMene
        items={[
          {
            onClick: onClickEditCustomerInfo,
            element: (
              <>
                <img
                  src={require('@/static/images/edit_no_margin.svg')}
                  style={{
                    width: 14,
                    marginRight: 12,
                  }}
                ></img>
                <p>{t('Edit')}</p>
              </>
            ),
          },
          {
            onClick: confirmAndDeleteCustomer,
            element: (
              <>
                <img
                  src={require('@/static/images/icon_delete_warning.svg')}
                  style={{
                    width: 20, // 画像（左右3px）分加算
                    marginRight: 8,
                    marginLeft: -3,
                  }}
                ></img>
                <p style={{ color: '#f44d10' }}>{t('Delete')}</p>
              </>
            ),
          },
        ]}
        direction="right"
      >
        <div css={editIconStyle}></div>
      </DropDownMene>
    </>
  )
}

const sectionStyle = css({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFF',
})

const listImageSectionStyle = css(sectionStyle, {
  padding: 0,
})

const listNameSectionStyle = css(sectionStyle, {
  ruby: {
    fontSize: 12,
    lineHeight: '29px',
  },
  width: '20%',
})

const listSectionStyle = css(sectionStyle, {
  borderLeft: '1px solid #F2F2F2',
  width: '30%',
})

const editIconStyle = css({
  display: 'block',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  borderRadius: '50px',
  width: '30px',
  height: '30px',
  backgroundImage: `url(${require('@/static/images/icon_other.svg')})`,
  cursor: 'pointer',
  ':hover': { backgroundColor: '#f2f2f2' },
})

const textStyle = css({
  color: '#272727',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

export const itemWrapperStyle = css(textStyle, {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 14,
  padding: '0 0 0 10px',
  img: {
    marginRight: 16,
    width: 18,
  },
  p: {
    fontSize: 14,
    height: 20,
    lineHeight: '21px',
  },
  span: {
    color: '#F2A40B',
  },
  ':nth-child(3n)': {
    marginBottom: 0,
  },
})

const imageStyle = css({
  margin: '0 auto',
  display: 'block',
  width: 58,
  borderRadius: '50%',
})

const nameStyle = css(textStyle, {
  // textAlign: 'center',
  fontWeight: 'bold',
  fontSize: 18,
})

const userAgeStyle = css({
  display: 'flex',
  marginTop: 12,
  color: '#676767',
  fontSize: 12,
})
